import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  TrendingUp as ProgressIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const PIPManagement = () => {
  const navigate = useNavigate();
  const userMeta = localStorage.getItem('login_meta');
  const { companyCode, branchId } = userMeta ? JSON.parse(userMeta) : {};

  const [pips, setPips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedPIP, setSelectedPIP] = useState(null);

  const [progressFormData, setProgressFormData] = useState({
    reviewDate: new Date().toISOString().split('T')[0],
    progress: 'ON_TRACK',
    achievements: [''],
    challenges: [''],
    actionItems: [''],
    reviewerComments: ''
  });

  const progressOptions = [
    { value: 'ON_TRACK', label: 'On Track', color: 'success' },
    { value: 'NEEDS_ATTENTION', label: 'Needs Attention', color: 'warning' },
    { value: 'OFF_TRACK', label: 'Off Track', color: 'error' }
  ];

  useEffect(() => {
    fetchPIPs();
  }, []);

  const fetchPIPs = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-performance/pip/list', {
        page: 1,
        limit: 100
      });
      if (response.data.success) {
        setPips(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching PIPs:', error);
      Swal.fire('Error', 'Failed to fetch PIPs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPIP = (pip) => {
    setSelectedPIP(pip);
    setViewDialogOpen(true);
  };

  const handleAddProgress = (pip) => {
    setSelectedPIP(pip);
    setProgressFormData({
      reviewDate: new Date().toISOString().split('T')[0],
      progress: 'ON_TRACK',
      achievements: [''],
      challenges: [''],
      actionItems: [''],
      reviewerComments: ''
    });
    setProgressDialogOpen(true);
  };

  const handleSubmitProgress = async () => {
    if (!selectedPIP) return;

    // Clean up empty arrays
    const cleanedData = {
      ...progressFormData,
      achievements: progressFormData.achievements.filter(a => a.trim()),
      challenges: progressFormData.challenges.filter(c => c.trim()),
      actionItems: progressFormData.actionItems.filter(a => a.trim())
    };

    try {
      const response = await axios.post(
        `/api/employee-performance/pip/progress/${selectedPIP._id}`,
        cleanedData
      );

      if (response.data.success) {
        Swal.fire('Success', 'Progress review added successfully', 'success');
        setProgressDialogOpen(false);
        fetchPIPs();
      }
    } catch (error) {
      console.error('Error adding progress:', error);
      Swal.fire('Error', 'Failed to add progress review', 'error');
    }
  };

  const handleCompletePIP = async (pip) => {
    const result = await Swal.fire({
      title: 'Complete PIP',
      html: `
        <div style="text-align: left;">
          <p><strong>Employee:</strong> ${pip.employeeId?.staff_name || pip.employeeId?.name || 'N/A'}</p>
          <p><strong>Duration:</strong> ${pip.pipDuration} days</p>
          <p>Select outcome:</p>
        </div>
      `,
      input: 'select',
      inputOptions: {
        'COMPLETED_SUCCESS': 'Successfully Completed',
        'COMPLETED_FAILURE': 'Not Successful'
      },
      inputPlaceholder: 'Select outcome',
      showCancelButton: true,
      confirmButtonText: 'Complete',
      inputValidator: (value) => {
        if (!value) {
          return 'Please select an outcome';
        }
      }
    });

    if (result.isConfirmed) {
      const { value: outcome } = await Swal.fire({
        title: 'Final Outcome',
        input: 'textarea',
        inputLabel: 'Describe the final outcome',
        inputPlaceholder: 'Enter detailed outcome...',
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return 'Please provide outcome details';
          }
        }
      });

      if (outcome) {
        const { value: nextSteps } = await Swal.fire({
          title: 'Next Steps',
          input: 'textarea',
          inputLabel: 'What are the next steps?',
          inputPlaceholder: 'Enter next steps...',
          showCancelButton: true
        });

        try {
          const response = await axios.post(
            `/api/employee-performance/pip/complete/${pip._id}`,
            {
              status: result.value,
              finalOutcome: outcome,
              nextSteps: nextSteps || ''
            }
          );

          if (response.data.success) {
            Swal.fire('Success', 'PIP completed successfully', 'success');
            fetchPIPs();
          }
        } catch (error) {
          console.error('Error completing PIP:', error);
          Swal.fire('Error', 'Failed to complete PIP', 'error');
        }
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'primary',
      COMPLETED_SUCCESS: 'success',
      COMPLETED_FAILURE: 'error',
      EXTENDED: 'warning',
      TERMINATED: 'default'
    };
    return colors[status] || 'default';
  };

  const calculateProgress = (pip) => {
    if (!pip.pipStartDate || !pip.pipEndDate) return 0;
    
    const start = new Date(pip.pipStartDate);
    const end = new Date(pip.pipEndDate);
    const now = new Date();
    
    const total = end - start;
    const elapsed = now - start;
    
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...progressFormData[field]];
    newArray[index] = value;
    setProgressFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleAddArrayField = (field) => {
    setProgressFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Performance Improvement Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/userdashboard/staff/hrm/performance/pip/create')}
          color="primary"
        >
          Create PIP
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active PIPs
              </Typography>
              <Typography variant="h4" color="warning.main">
                {pips?.filter(p => p.status === 'ACTIVE').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed (Success)
              </Typography>
              <Typography variant="h4" color="success.main">
                {pips?.filter(p => p.status === 'COMPLETED_SUCCESS').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed (Failure)
              </Typography>
              <Typography variant="h4" color="error.main">
                {pips?.filter(p => p.status === 'COMPLETED_FAILURE').length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total PIPs
              </Typography>
              <Typography variant="h4">
                {pips?.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* PIP Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Reviews</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : !pips || pips.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No PIPs found
                </TableCell>
              </TableRow>
            ) : (
              pips.map((pip) => (
                <TableRow key={pip._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {pip.employeeId?.staff_name || pip.employeeId?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {pip.employeeId?.emp_id || pip.employeeId?.employeeCode || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(pip.pipStartDate).toLocaleDateString()} - {new Date(pip.pipEndDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {pip.pipDuration} days
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">
                          {calculateProgress(pip).toFixed(0)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={calculateProgress(pip)}
                        color={pip.status === 'ACTIVE' ? 'primary' : 'success'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${pip.progressReviews?.length || 0} reviews`}
                      size="small"
                      color="info"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pip.status.replace(/_/g, ' ')}
                      size="small"
                      color={getStatusColor(pip.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewPIP(pip)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {pip.status === 'ACTIVE' && (
                        <>
                          <Tooltip title="Add Progress Review">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleAddProgress(pip)}
                            >
                              <ProgressIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Complete PIP">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleCompletePIP(pip)}
                            >
                              <CompleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View PIP Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>PIP Details</DialogTitle>
        <DialogContent>
          {selectedPIP && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Employee Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedPIP.employeeId?.staff_name || selectedPIP.employeeId?.name || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Code:</strong> {selectedPIP.employeeId?.emp_id || selectedPIP.employeeId?.employeeCode || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Performance Issues
                  </Typography>
                  <List dense>
                    {selectedPIP.performanceIssues?.map((issue, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={issue.issue}
                          secondary={
                            <>
                              <Chip
                                label={issue.severity}
                                size="small"
                                color={
                                  issue.severity === 'CRITICAL' ? 'error' :
                                  issue.severity === 'HIGH' ? 'warning' : 'default'
                                }
                                sx={{ mr: 1 }}
                              />
                              {issue.description}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Objectives
                  </Typography>
                  <List dense>
                    {selectedPIP.objectives?.map((obj, index) => {
                      const parts = [];
                      if (obj.targetValue) parts.push(`Target: ${obj.targetValue}`);
                      if (obj.targetDate) parts.push(`Due: ${new Date(obj.targetDate).toLocaleDateString()}`);
                      const secondary = parts.length > 0 ? parts.join(' | ') : 'No target or deadline set';
                      
                      return (
                        <ListItem key={index}>
                          <ListItemText
                            primary={obj.objective}
                            secondary={secondary}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Progress Reviews ({selectedPIP.progressReviews?.length || 0})
                  </Typography>
                  {selectedPIP.progressReviews?.map((review, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2">
                            Review #{index + 1}
                          </Typography>
                          <Chip
                            label={review.progress}
                            size="small"
                            color={
                              review.progress === 'ON_TRACK' ? 'success' :
                              review.progress === 'NEEDS_ATTENTION' ? 'warning' : 'error'
                            }
                          />
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(review.reviewDate).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Achievements:</strong> {review.achievements?.join(', ')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Challenges:</strong> {review.challenges?.join(', ')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Comments:</strong> {review.reviewerComments}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Progress Dialog */}
      <Dialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Progress Review</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Review Date"
                value={progressFormData.reviewDate}
                onChange={(e) => setProgressFormData(prev => ({ ...prev, reviewDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Progress Status"
                value={progressFormData.progress}
                onChange={(e) => setProgressFormData(prev => ({ ...prev, progress: e.target.value }))}
              >
                {progressOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Achievements
              </Typography>
              {progressFormData.achievements.map((achievement, index) => (
                <TextField
                  key={index}
                  fullWidth
                  size="small"
                  value={achievement}
                  onChange={(e) => handleArrayFieldChange('achievements', index, e.target.value)}
                  placeholder="Enter achievement..."
                  sx={{ mb: 1 }}
                />
              ))}
              <Button size="small" onClick={() => handleAddArrayField('achievements')}>
                + Add Achievement
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Challenges
              </Typography>
              {progressFormData.challenges.map((challenge, index) => (
                <TextField
                  key={index}
                  fullWidth
                  size="small"
                  value={challenge}
                  onChange={(e) => handleArrayFieldChange('challenges', index, e.target.value)}
                  placeholder="Enter challenge..."
                  sx={{ mb: 1 }}
                />
              ))}
              <Button size="small" onClick={() => handleAddArrayField('challenges')}>
                + Add Challenge
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Action Items
              </Typography>
              {progressFormData.actionItems.map((item, index) => (
                <TextField
                  key={index}
                  fullWidth
                  size="small"
                  value={item}
                  onChange={(e) => handleArrayFieldChange('actionItems', index, e.target.value)}
                  placeholder="Enter action item..."
                  sx={{ mb: 1 }}
                />
              ))}
              <Button size="small" onClick={() => handleAddArrayField('actionItems')}>
                + Add Action Item
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reviewer Comments"
                value={progressFormData.reviewerComments}
                onChange={(e) => setProgressFormData(prev => ({ ...prev, reviewerComments: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitProgress}>
            Add Progress Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PIPManagement;
